// src/demo/DemoMail.tsx: "See the Welcome Email" (docs/DECISIONS.md, "Demo tour"). The demo captures every email
// instead of sending it, so a builder playing the buyer never gets the emails a family would; this shows each one
// where it would arrive: a marked demo row opening the captured email in the product's own sheet.
//   the welcome email   the welcome sheet and the tour's stop menu; its Open My Board leads back into his board
//   the sign-in email   "Check Your Email" (confirming it's you); its link gives the full session
//   the confirmation    "Confirm Your Email First" (inviting someone): the newest welcome or confirmation email
//   the code email      Start Your Build's known-email page (the same address a second time): its six digits
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import type { DemoOutboxItem, DemoOutboxRow } from "../../shared/api";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { cx } from "../ui/cx";
import { Spinner } from "../ui/Feedback";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";
import { EMAIL_SANDBOX, emailDocument } from "./email-frame";
import "../styles/demo-bar.css";

/**
 * Which captured email: the template or templates (worker/src/email), its board, and `mine` (the signed-in person's
 * own) or `to` (an address typed on the page): the server matches the real address; the list masks it, so the page
 * can't.
 */
export type MailQuery = { template: string | readonly string[]; pid?: string; mine?: boolean; to?: string };

/** The email a new board's family gets. */
export const WELCOME = "workspaceFromForm";

/** The newest captured email of the template(s) and board (the outbox lists newest first). */
export function pickMail(rows: readonly DemoOutboxRow[], q: MailQuery): DemoOutboxRow | null {
  const templates = typeof q.template === "string" ? [q.template] : q.template;
  return rows.find((r) => templates.includes(r.template) && (!q.pid || r.projectId === q.pid)) ?? null;
}

/** The email on screen, if any. One sheet for the whole app (DemoMailHost in App.tsx), so a row inside another sheet
 *  (the welcome sheet, "Check Your Email") opens it over everything, never inside that sheet. */
const shown = signal<MailQuery | null>(null);

/** The demo row that opens a captured email (the welcome email for board `pid`, unless told otherwise). */
export function DemoPeek(props: { pid?: string; template?: MailQuery["template"]; mine?: boolean; to?: string; label?: string; class?: string; onOpen?: () => void }) {
  const label = props.label ?? "See the Welcome Email";
  return (
    <button
      type="button"
      class={cx("demo-peek", props.class)}
      onClick={() => {
        props.onOpen?.();
        shown.value = { template: props.template ?? WELCOME, pid: props.pid, mine: props.mine, to: props.to };
      }}
      aria-label={`Demo: ${label.toLowerCase()}`}
    >
      <span class="db-tag">Demo</span>
      <span class="t">{label}</span>
      <Icon name="arrow-r" />
    </button>
  );
}

/** Mounted once in App.tsx: the captured email a demo row asked for. */
export function DemoMailHost() {
  const q = shown.value;
  const [last, setLast] = useState<MailQuery | null>(q);
  useEffect(() => {
    if (q) setLast(q);
  }, [q]);
  if (!last) return null;
  return <DemoMailSheet query={last} open={!!q} onClose={() => (shown.value = null)} />;
}

type Loaded = { state: "loading" } | { state: "none" } | { state: "error" } | { state: "ok"; item: DemoOutboxItem };

/** The newest captured email that matches (the outbox lists newest first). */
export function DemoMailSheet(props: { query: MailQuery; open: boolean; onClose: () => void }) {
  const { pid, mine, to } = props.query;
  const template = typeof props.query.template === "string" ? props.query.template : props.query.template.join(",");
  const [mail, setMail] = useState<Loaded>({ state: "loading" });
  useEffect(() => {
    if (!props.open) return;
    let live = true;
    setMail({ state: "loading" });
    void (async () => {
      try {
        const { rows } = await api.demoOutbox({ mine, to });
        const row = pickMail(rows, props.query);
        if (!row) {
          if (live) setMail({ state: "none" });
          return;
        }
        const item = await api.demoOutboxItem(row.id);
        if (live) setMail(item.html ? { state: "ok", item } : { state: "none" });
      } catch {
        if (live) setMail({ state: "error" });
      }
    })();
    return () => {
      live = false;
    };
  }, [props.open, template, pid, mine, to]);

  const item = mail.state === "ok" ? mail.item : null;
  return (
    <Sheet
      open={props.open}
      onClose={props.onClose}
      class="demo-mail"
      closeLabel="Close"
      meta={
        <span class="dm-k">
          <span class="db-tag">Demo</span>
          Captured, not sent
        </span>
      }
      title={item?.subject ?? (template === WELCOME ? "The Welcome Email" : "The Email")}
      sub={item ? `To ${item.toEmail} · ${formatDateTime(item.createdAt)}` : undefined}
    >
      {mail.state === "loading" ? (
        <div class="dm-empty">
          <Spinner label="Opening the email" />
        </div>
      ) : item ? (
        <iframe title={item.subject} sandbox={EMAIL_SANDBOX} srcdoc={emailDocument(item.html)} referrerpolicy="no-referrer" />
      ) : (
        <p class="dm-empty">{mail.state === "error" ? "The email didn't load. Try again." : "The email isn't here yet."}</p>
      )}
    </Sheet>
  );
}
